import React, { useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import ImageIcon from '@material-ui/icons/Image';
import CloseIcon from '@material-ui/icons/Close';

const formatDateTime = (isoString) => {
  if (!isoString) {
    return '-';
  }

  try {
    const localized = new Date(isoString);
    return localized.toLocaleString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return isoString;
  }
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return '-';
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startFormatted = start.toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const endFormatted = end.toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    return '-';
  }
};

const formatRatio = (value) => {
  if (!isFinite(value)) {
    return '0%';
  }
  return `${(value * 100).toFixed(2)}%`;
};

const calculateRatio = (pageloads, registrations) => {
  if (!pageloads || pageloads <= 0) {
    return 0;
  }
  return registrations / pageloads;
};

const formatRelative = (value) => {
  const percentage = value * 100;
  const formatted = percentage.toFixed(2);
  if (percentage > 0) {
    return `+${formatted}%`;
  }
  return `${formatted}%`;
};

const ImageTooltip = ({ landing }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);

  const handleMouseEnter = () => {
    if (!iconRef.current) return;
    
    const rect = iconRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = 300;
    const tooltipHeight = 150;
    const offset = 8;
    
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    let top = rect.top - tooltipHeight - offset;
    
    if (left < 10) {
      left = 10;
    }
    if (left + tooltipWidth > viewportWidth - 10) {
      left = viewportWidth - tooltipWidth - 10;
    }
    if (top < 10) {
      top = rect.bottom + offset;
    }
    if (top + tooltipHeight > viewportHeight - 10) {
      top = Math.max(10, viewportHeight - tooltipHeight - 10);
    }
    
    setTooltipPosition({ top: Math.max(10, top), left: Math.max(10, left) });
    setShowTooltip(true);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setShowTooltip(false);
  };

  const tooltipContent = showTooltip && (
    <div
      style={{
        position: 'fixed',
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        zIndex: 99999,
        width: '300px',
        height: '150px',
        backgroundColor: '#f3f4f6',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        padding: '0',
        cursor: 'pointer',
        backgroundImage: landing.image_path ? `url(${landing.image_path})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
      onClick={() => {
        if (landing.image_path) {
          window.open(landing.image_path, '_blank');
        }
      }}
      title="Click to open in new tab"
    >
      <button
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: '4px',
          right: '4px',
          background: 'rgba(0, 0, 0, 0.6)',
          border: 'none',
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
          zIndex: 100000
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
        }}
        title="Close"
      >
        <CloseIcon style={{ color: '#fff', fontSize: '16px' }} />
      </button>
    </div>
  );

  return (
    <>
      <div 
        ref={iconRef}
        style={{ position: 'relative', display: 'inline-block' }}
        onMouseEnter={handleMouseEnter}
      >
        <ImageIcon 
          style={{ 
            color: '#10b981', 
            cursor: 'pointer',
            width: '20px',
            height: '20px'
          }}
        />
      </div>
      {typeof document !== 'undefined' && tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  );
};

const AbTestsList = ({ tests, pagination, onPageChange, onDelete, deletingId }) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const sortedTests = useMemo(() => {
    if (!Array.isArray(tests)) {
      return [];
    }

    return [...tests].sort((a, b) => {
      const dateA = new Date(a.start_date || a.created_at || a.date || 0).getTime();
      const dateB = new Date(b.start_date || b.created_at || b.date || 0).getTime();
      return dateB - dateA;
    });
  }, [tests]);

  const toggleExpanded = (testId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  };

  const totalPages = pagination && typeof pagination.total_pages === "number" ? pagination.total_pages : 1;
  const currentPage = pagination && typeof pagination.page === "number" ? pagination.page : 1;
  const hasPagination = totalPages > 1;

  if (!sortedTests.length) {
    return <div>No A/B tests yet.</div>;
  }

  return (
    <div className="ab-tests-list flex flex-col gap-4 w-full">
      {sortedTests.map((test) => {
        const isExpanded = expandedIds.has(test.id);
        const geoLabel = !test.geo || test.geo.toLowerCase() === 'all' ? 'All countries' : test.geo;
        const landings = Array.isArray(test.landings) ? test.landings : [];
        const baseLanding = landings[0];
        const baseRatio = baseLanding ? calculateRatio(baseLanding.pageloads, baseLanding.registrations) : 0;

        return (
          <div key={test.id} className="w-full max-w-full border border-gray-200 rounded-md shadow-sm bg-white overflow-hidden">
            <button
              type="button"
              className="w-full px-6 py-4 text-left hover:bg-gray-50 transition"
              onClick={() => toggleExpanded(test.id)}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center w-full">
                <div className="flex items-center gap-2 font-semibold text-gray-800 text-lg truncate">
                  <span className="truncate">{test.name}</span>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="block uppercase text-xs text-gray-400">Date</span>
                  <span>{formatDateRange(test.start_date, test.end_date)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 text-sm text-gray-600">
                  <div>
                    <span className="block uppercase text-xs text-gray-400">Geo</span>
                    <span>{geoLabel}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-800 font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (onDelete) { onDelete(test); }
                      }}
                      disabled={Boolean(deletingId) && deletingId !== test.id}
                    >
                      {deletingId === test.id ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800 font-semibold whitespace-nowrap"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleExpanded(test.id);
                      }}
                    >
                      {isExpanded ? 'Collapse' : 'Expand'}
                    </button>
                  </div>
                </div>
              </div>
            </button>
            {isExpanded && (
              <div className="px-6 pb-6" style={{ overflowX: 'auto' }}>
                {landings.length > 0 ? (
                  <table className="min-w-full table-auto border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-1/5 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Landing</th>
                        <th className="w-1/5 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page Loads</th>
                        <th className="w-1/5 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registrations</th>
                        <th className="w-1/5 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ratio</th>
                        <th className="w-1/5 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Diff Main</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '60px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {landings.map((landing, index) => {
                        const hasImage = landing.image_path && landing.image_path.trim();
                        return (
                          <tr key={landing.id || landing.name} className="odd:bg-white even:bg-gray-50">
                            <td className="w-1/5 px-4 py-2 text-sm text-gray-700 break-words">{landing.name}</td>
                            <td className="w-1/5 px-4 py-2 text-sm text-gray-700">{landing.pageloads}</td>
                            <td className="w-1/5 px-4 py-2 text-sm text-gray-700">{landing.registrations}</td>
                            <td className="w-1/5 px-4 py-2 text-sm text-gray-700">{formatRatio(calculateRatio(landing.pageloads, landing.registrations))}</td>
                            <td className="w-1/5 px-4 py-2 text-sm text-gray-700">{index === 0 || baseRatio === 0 ? '-' : formatRelative(calculateRatio(landing.pageloads, landing.registrations) / baseRatio - 1)}</td>
                            <td className="px-4 py-2 text-center" style={{ position: 'relative' }}>
                              {hasImage && (
                                <ImageTooltip landing={landing} />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-sm text-gray-500">No landing data for this test.</div>
                )}
                <div className="mt-6">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Comment</div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {test.comment && test.comment.trim() ? test.comment : '-'}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {hasPagination && (
        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            className="px-4 py-2 rounded border border-gray-300 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => { if (onPageChange) { onPageChange(currentPage - 1); } }}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages} ({pagination && typeof pagination.total_count === "number" ? pagination.total_count : sortedTests.length} tests)
          </span>
          <button
            type="button"
            className="px-4 py-2 rounded border border-gray-300 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => { if (onPageChange) { onPageChange(currentPage + 1); } }}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AbTestsList;
